import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { getProductByHandle, getBestsellers } from '../services/productService';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useMarket } from '../context/MarketContext';
import { formatPrice, calcDiscount } from '../utils/formatPrice';
import { stockStatus } from '../utils/stockStatus';
import ProductRating from '../components/reviews/ProductRating';
import ProductReviewsPanel from '../components/reviews/ProductReviewsPanel';
import ProductOfferStrip from '../components/offers/ProductOfferStrip';
import { useReviews } from '../context/ReviewsContext';
import QuantitySelector from '../components/common/QuantitySelector';
import ProductCard from '../components/product/ProductCard';
import { trackViewItem, remarketingProductPage } from '../utils/analytics';
import {
  BeakerIcon, HeartIcon, CheckBadgeIcon, ShieldCheckIcon, TruckIcon, BanknotesIcon, ArrowPathIcon, ShoppingBagIcon,
  FaceFrownIcon,
} from '@heroicons/react/24/outline';

const TRUST_BADGES = [
  { Icon: BeakerIcon,      label: 'Dermatologist Tested' },
  { Icon: HeartIcon,       label: 'Cruelty Free' },
  { Icon: CheckBadgeIcon,  label: 'Clinically Proven' },
  { Icon: ShieldCheckIcon, label: '100% Authentic' },
];

const ProductDetailPage = () => {
  const { handle }   = useParams();
  const navigate     = useNavigate();
  const { addItem }  = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { currentMarket } = useMarket();

  const marketCode = currentMarket?.isDefault ? null : currentMarket?.code;

  const fmtPrice = useCallback((amount) => {
    const symbol = currentMarket?.currencySymbol;
    if (symbol && symbol !== '₹') {
      return `${symbol}${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return formatPrice(amount);
  }, [currentMarket]);

  const [product,        setProduct]        = useState(null);
  const [related,        setRelated]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [selectedImage,  setSelectedImage]  = useState(0);
  const [selectedVariant,setSelectedVariant]= useState(0);
  const [quantity,       setQuantity]       = useState(1);
  const [activeTab,      setActiveTab]      = useState('description');
  const { ratingsPublic, ratingFor } = useReviews();
  const [addState,       setAddState]       = useState('idle');
  const [showStickyBar,  setShowStickyBar]  = useState(false);
  const ctaRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setProduct(null);
    setRelated([]);
    setSelectedImage(0);
    setSelectedVariant(0);

    Promise.all([
      getProductByHandle(handle, marketCode),
      getBestsellers(8, marketCode),
    ]).then(([p, bestsellers]) => {
      if (cancelled) return;
      setProduct(p);
      if (p) {
        setRelated(
          bestsellers
            .filter((r) => r.id !== p.id && (r.type === p.type || r.vendor === p.vendor))
            .slice(0, 4)
        );
        trackViewItem(p, p.variants?.[0] ?? null);
        remarketingProductPage(p, p.variants?.[0] ?? null);
      }
    }).catch(() => {}).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [handle, marketCode]);

  /* Sticky add-to-cart on mobile */
  useEffect(() => {
    if (!ctaRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, [product]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2 mb-6">
          {[60, 8, 48, 8, 72, 8, 140].map((w, i) =>
            w === 8
              ? <span key={i} className="text-gray-200">/</span>
              : <div key={i} className="shimmer h-3 rounded-full" style={{ width: w }} />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-14 mb-16">
          {/* Image skeleton */}
          <div className="space-y-3">
            <div className="aspect-square rounded-2xl shimmer" />
            <div className="flex gap-2">
              {[0,1,2,3].map(i => <div key={i} className="w-20 h-20 rounded-xl shimmer flex-shrink-0" />)}
            </div>
          </div>

          {/* Info skeleton */}
          <div className="space-y-4">
            <div className="shimmer h-3 w-24 rounded-full" />
            <div className="space-y-2">
              <div className="shimmer h-7 w-full rounded-xl" />
              <div className="shimmer h-7 w-3/4 rounded-xl" />
            </div>
            <div className="flex gap-2">
              {[0,1,2,3].map(i => <div key={i} className="shimmer h-8 w-20 rounded-full" />)}
            </div>
            <div className="shimmer h-10 w-48 rounded-xl" />
            <div className="shimmer h-4 w-40 rounded-full" />
            <div className="shimmer h-px w-full rounded-full mt-2" style={{ background: '#e5e7eb' }} />
            <div className="shimmer h-12 rounded-xl mt-4" />
            <div className="flex gap-3 mt-2">
              <div className="shimmer h-14 flex-1 rounded-full" />
              <div className="shimmer h-14 flex-1 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 rounded-full bg-primary-light flex items-center justify-center mx-auto mb-5">
          <FaceFrownIcon className="w-9 h-9 text-primary" aria-hidden="true" />
        </div>
        <h1 className="display-title text-3xl mb-2">Product Not Found</h1>
        <p className="text-ink-soft mb-7">This product may have been removed or the URL is incorrect.</p>
        <button onClick={() => navigate('/shop')} className="btn-primary">Browse All Products</button>
      </div>
    );
  }

  // images are already URL strings after normalizeProduct
  const images   = product.images?.length ? product.images : [];
  const variants = product.variants || [];
  const variant  = variants[selectedVariant] || null;

  // Price: use market price when available, fall back to base price
  const price        = Number(variant?.marketPrice ?? variant?.price ?? product.price) || 0;
  const compareAt    = Number(variant?.marketCompareAtPrice ?? variant?.compareAtPrice ?? product.compareAtPrice) || 0;
  const discount     = calcDiscount(price, compareAt || null);
  const savings      = compareAt > price ? compareAt - price : 0;
  const wishlisted   = isWishlisted(product.id);

  // Stock: selected variant's qty > product total > 0
  const stockQty    = variant?.inventoryQuantity ?? product.inventory ?? 0;
  const continueOOS = variant?.continueSellingWhenOutOfStock
    ?? product.variants?.[0]?.continueSellingWhenOutOfStock
    ?? false;
  const { isOOS, isLowStock, maxQty } = stockStatus(stockQty, continueOOS);

  const handleAddToCart = async () => {
    if (addState !== 'idle' || isOOS) return;
    setAddState('adding');
    await new Promise(r => setTimeout(r, 400));
    addItem(
      { ...product, price, marketPrice: price, marketCurrencyCode: product.marketCurrencyCode, marketCurrencySymbol: product.marketCurrencySymbol, images },
      variant?.size || variant?.title || 'Default',
      quantity
    );
    toast.success('Added to cart!');
    setAddState('done');
    setTimeout(() => setAddState('idle'), 1800);
  };

  const handleBuyNow = () => {
    if (isOOS) return;
    addItem(
      { ...product, price, marketPrice: price, marketCurrencyCode: product.marketCurrencyCode, marketCurrencySymbol: product.marketCurrencySymbol, images },
      variant?.size || variant?.title || 'Default',
      quantity
    );
    navigate('/checkout');
  };

  return (
    <>
      <Helmet>
        <title>{product.title + ' — Skinn Thrive'}</title>
        <meta name="description" content={(product.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 160)} />
        <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/product/${product.handle}`} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Skinn Thrive" />
        <meta property="og:title" content={product.title + ' — Skinn Thrive'} />
        <meta property="og:description" content={(product.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 160)} />
        <meta property="og:url" content={`${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/product/${product.handle}`} />
        <meta property="og:image" content={images[0] || ''} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="800" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={product.title + ' — Skinn Thrive'} />
        <meta name="twitter:description" content={(product.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 160)} />
        <meta name="twitter:image" content={images[0] || ''} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.title,
          description: (product.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 500),
          image: images,
          brand: { '@type': 'Brand', name: product.vendor || 'Skinn Thrive' },
          sku: product.variants?.[selectedVariant]?.sku || String(product.id),
          ...(ratingFor(product.id) ? { aggregateRating: {
            '@type': 'AggregateRating', ratingValue: ratingFor(product.id).average, reviewCount: ratingFor(product.id).count,
          } } : {}),
          offers: {
            '@type': 'Offer',
            priceCurrency: 'INR',
            price: price,
            availability: isOOS
              ? 'https://schema.org/OutOfStock'
              : 'https://schema.org/InStock',
            url: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/product/${product.handle}`,
            seller: { '@type': 'Organization', name: 'Skinn Thrive' },
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/` },
            { '@type': 'ListItem', position: 2, name: 'Shop', item: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/shop` },
            { '@type': 'ListItem', position: 3, name: product.type || 'Products', item: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/shop?type=${encodeURIComponent(product.type || '')}` },
            { '@type': 'ListItem', position: 4, name: product.title, item: `${process.env.REACT_APP_SITE_URL || 'https://skinnthrive.com'}/product/${product.handle}` },
          ],
        })}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-ink-muted mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-primary">Shop</Link>
          <span>/</span>
          <Link to={`/shop?type=${product.type}`} className="hover:text-primary">{product.type}</Link>
          <span>/</span>
          <span className="text-ink truncate max-w-[200px]">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-14 mb-16">

          {/* ── Image gallery ── */}
          <div className="space-y-3">
            <div className="aspect-square rounded-3xl overflow-hidden bg-[#F6F4F9] relative border border-[#EDEAF2]">
              {images.length > 0 ? (
                <img
                  key={images[selectedImage]}
                  src={images[selectedImage]}
                  alt={product.title}
                  fetchpriority="high"
                  className={`w-full h-full object-contain p-8 mix-blend-multiply hover:scale-105 transition-transform duration-500 ${isOOS ? 'opacity-50 grayscale' : ''}`}
                  // a broken photo is hidden — never swapped for a stock photo of some other product
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <svg className="w-20 h-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
              )}
              <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                {isOOS && (
                  <span className="badge bg-red-500 text-white px-3 py-1 text-xs font-semibold">Out of Stock</span>
                )}
                {isLowStock && (
                  <span className="badge bg-amber-500 text-white px-2.5 py-1 text-xs">Only {stockQty} left!</span>
                )}
              </div>
              <button
                onClick={() => toggleWishlist(product)}
                aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-all
                  ${wishlisted ? 'bg-primary text-white' : 'bg-white text-ink-soft border border-[#EDEAF2] hover:text-primary'}`}
              >
                <svg className="w-5 h-5" fill={wishlisted ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                </svg>
              </button>
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)}
                    className={`shrink-0 w-20 h-20 rounded-2xl overflow-hidden border-2 transition-all bg-[#F6F4F9]
                      ${i === selectedImage ? 'border-primary' : 'border-transparent hover:border-[#DDD6FE]'}`}>
                    <img src={img} alt={`${product.title} — ${i + 1}`} loading="lazy" className="w-full h-full object-contain p-2 mix-blend-multiply" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Product info ── */}
          <div>
            <div className="mb-1">
              <Link to={`/collections/${(product.vendor || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                className="text-xs font-bold uppercase tracking-widest text-primary hover:underline">
                {product.vendor}
              </Link>
            </div>
            <h1 className="display-title text-[1.6rem] md:text-[2.1rem] mb-3">
              {product.title}
            </h1>

            <ProductRating productId={product.id} size="md" className="mb-5" />

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-1">
              <span className="text-3xl font-semibold text-ink">{fmtPrice(price)}</span>
              {compareAt > price && (
                <span className="text-lg text-ink-muted line-through">{fmtPrice(compareAt)}</span>
              )}
              {discount > 0 && (
                <span className="badge bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-xs">{discount}% off</span>
              )}
            </div>
            {savings > 0 && (
              <p className="text-emerald-700 font-medium text-sm mb-1">
                You save {fmtPrice(savings)}
              </p>
            )}
            <p className="text-xs text-ink-muted mb-5">Inclusive of all taxes. {(currentMarket?.freeShippingThreshold ?? 699) === 0 ? 'Free shipping on all orders.' : `Free shipping on orders above ${fmtPrice(currentMarket?.freeShippingThreshold ?? 699)}.`}</p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-2 mb-5">
              {TRUST_BADGES.map(({ Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 bg-white border border-[#EDEAF2] text-ink-soft text-xs font-medium px-3 py-1.5 rounded-full">
                  <Icon className="w-3.5 h-3.5 text-primary" aria-hidden="true" />{label}
                </span>
              ))}
            </div>

            <div className="mb-5 [&>div]:mt-0"><ProductOfferStrip product={{ ...product, price, marketPrice: price }} /></div>

            {/* Variants */}
            {variants.length > 1 && (
              <div className="mb-5">
                <p className="text-sm font-semibold text-ink mb-2">Size / Pack</p>
                <div className="flex gap-2 flex-wrap">
                  {variants.map((v, i) => (
                    <button key={i} onClick={() => setSelectedVariant(i)}
                      className={`px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all
                        ${i === selectedVariant ? 'border-primary bg-primary-light text-primary' : 'border-border text-ink-soft hover:border-primary'}`}>
                      {v.size || v.title} — {fmtPrice(v.marketPrice ?? v.price)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Skin type tags */}
            {product.skinType?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {product.skinType.map(st => (
                  <span key={st} className="badge bg-primary-light text-primary text-xs">{st}</span>
                ))}
              </div>
            )}

            {/* Quantity + CTA */}
            <div ref={ctaRef}>
              {isOOS ? (
                <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-2xl text-center">
                  <p className="text-red-600 font-semibold text-sm mb-1">Currently Out of Stock</p>
                  <p className="text-xs text-red-400">Check back soon or explore similar products below.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <QuantitySelector value={quantity} onChange={setQuantity} min={1} max={maxQty} />
                    <p className={`text-xs font-medium ${isLowStock ? 'text-amber-600' : 'text-ink-muted'}`}>
                      {isLowStock ? `Only ${stockQty} left — order soon` : stockStatus(stockQty, continueOOS).label}
                    </p>
                  </div>
                  <div className="flex gap-3 mb-5">
                    <button
                      onClick={handleAddToCart}
                      className={`flex-1 h-[50px] rounded-full font-semibold text-sm flex items-center justify-center gap-2 border-2 transition-all active:scale-[.97]
                        ${addState === 'done' ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : 'border-primary text-primary hover:bg-primary hover:text-white'}`}
                    >
                      {addState === 'adding' ? (
                        <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Adding…</>
                      ) : addState === 'done' ? '✓ Added to Cart' : <><ShoppingBagIcon className="w-[18px] h-[18px]" aria-hidden="true" />Add to Cart</>}
                    </button>
                    <button
                      onClick={handleBuyNow}
                      className="flex-1 h-[50px] rounded-full font-semibold text-sm flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-dark active:scale-[.97] transition-all shadow-sm hover:shadow-md"
                    >Buy Now</button>
                  </div>
                </>
              )}
            </div>

            {/* Delivery info */}
            <div className="bg-[#FBFAFD] border border-[#EDEAF2] rounded-2xl p-4 md:p-5 space-y-2.5">
              {[
                [TruckIcon, currentMarket?.isDefault ? 'Express delivery in 2–4 business days' : 'International delivery in 5–7 business days'],
                ...(currentMarket?.isDefault ? [[BanknotesIcon, 'Cash on Delivery available']] : []),
                [ArrowPathIcon, 'Easy returns within 7 days'],
                [ShieldCheckIcon, '100% authentic — sealed & verified'],
              ].map(([Icon, text]) => (
                <div key={text} className="flex items-center gap-3 text-sm text-ink-soft">
                  <Icon className="w-[18px] h-[18px] text-primary shrink-0" aria-hidden="true" /><span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="mb-16">
          <div className="flex gap-0 border-b border-border mb-7 overflow-x-auto scrollbar-hide">
            {['description','ingredients','how-to-use', ...(ratingsPublic ? ['reviews'] : [])].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-5 py-3 text-sm font-semibold capitalize whitespace-nowrap border-b-2 -mb-px transition-all
                  ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-ink-soft hover:text-ink'}`}>
                {tab.replace('-',' ')}
              </button>
            ))}
          </div>
          <div className="text-sm text-ink-soft leading-relaxed max-w-3xl">
            {activeTab === 'description' && <p>{product.description || 'No description available.'}</p>}
            {activeTab === 'ingredients' && (
              <p>Key ingredients may include ceramides, hyaluronic acid, niacinamide, and other clinically tested active compounds specific to this formulation. Always check the product label for the full INCI list.</p>
            )}
            {activeTab === 'how-to-use' && (
              <ol className="list-decimal pl-5 space-y-2">
                <li>Cleanse your face with a gentle face wash and pat dry.</li>
                <li>Apply a small, pea-sized amount to the affected area.</li>
                <li>Gently massage in upward circular motions until fully absorbed.</li>
                <li>Follow with SPF during the day if using AM. Use as directed by your dermatologist.</li>
              </ol>
            )}
            {activeTab === 'reviews' && ratingsPublic && <ProductReviewsPanel productId={product.id} />}
          </div>
        </div>

        {/* ── Related products ── */}
        {related.length > 0 && (
          <div>
            <div className="mb-8">
              <div className="w-10 h-1 bg-primary rounded-full mb-3" />
              <h2 className="text-2xl font-bold text-ink">You May Also Like</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>

      {/* Sticky mobile CTA bar */}
      {showStickyBar && (
        <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-border px-4 py-3 z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-ink-muted truncate">{product.vendor}</p>
              <p className="text-sm font-bold text-ink truncate">{fmtPrice(price)}</p>
            </div>
            {isOOS ? (
              <span className="text-sm font-semibold text-red-500 shrink-0">Out of Stock</span>
            ) : (
              <>
                <button onClick={handleAddToCart} className="btn-outline btn-sm w-[96px]">Add</button>
                <button onClick={handleBuyNow}    className="btn-primary btn-sm w-[96px]">Buy Now</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDetailPage;
